/**
 * Alert Settings Component
 * Configuration for inventory alert thresholds
 * Location: Settings → Inventory → Alert Settings
 */

import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

// Shared components
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  SearchFormComponent,
  FormFieldConfig,
} from '@app/shared/components/search-form/search-form.component';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { ConfirmationDialogComponent } from '@app/shared/components/confirmation-dialog/confirmation-dialog.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import {
  ALERT_SETTINGS_COLUMNS,
  ALERT_SETTINGS_DEFAULT_COLUMNS_ORDER,
  ALERT_SETTINGS_FLEXIBLE_COLUMNS_ORDER,
  ALERT_SETTINGS_ACTION_COLUMN,
} from './alert-settings.config';

// Services
import { AlertSettingsService } from './services/alert-settings.service';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { SkuOptionsService } from '@shared/services/sku-options.service';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import { AlertSettingsDialogComponent } from './components/alert-settings-dialog/alert-settings-dialog.component';
import { ThemeService } from '@app/core/core';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { extractErrorMessage } from '@app/shared/utils/error-handler.util';
import { PermissionService } from '@app/core/services/permission.service';

// Types
export interface AlertConfig {
  id?: number;
  scope?: string;
  skuCode?: string;
  skuName?: string; // Tên SKU (tên sản phẩm)
  productName?: string; // Tên sản phẩm (SPU name)
  warehouseCode?: string;
  warehouseName?: string; // Tên kho
  name?: string; // Tên cấu hình
  description?: string; // Mô tả
  minStock?: number;
  maxStock?: number;
  lastUpdated?: Date | string;
  creationDate?: string;
}

export interface AlertConfigSearchParams {
  skuCode?: string;
  warehouseCode?: string;
  name?: string;
}

@Component({
  selector: 'app-alert-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    AppButtonComponent,
    AdvancedDataTableComponent,
    SearchFormComponent,
    ToastModule,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService, DialogService],
  templateUrl: './alert-settings.component.html',
  styleUrls: ['./alert-settings.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertSettingsComponent implements OnInit, OnDestroy {
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly dialogService = inject(DialogService);
  private readonly alertSettingsService = inject(AlertSettingsService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  protected readonly themeService = inject(ThemeService);
  private readonly permissionService = inject(PermissionService);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly skuOptionsService = inject(SkuOptionsService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using menu codes: settings.inventory.alert_settings.view/create/edit/delete
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.inventory.alert_settings.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.inventory.alert_settings.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.inventory.alert_settings.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.inventory.alert_settings.delete');
  });

  // Check if user has any action permissions (only edit or delete, no view action)
  hasAnyActionPermission = computed(
    () => this.canUpdate() || this.canDelete(),
  );

  private dialogRef: DynamicDialogRef | null = null;

  // Data signals
  alertConfigs = signal<AlertConfig[]>([]);
  filteredConfigs = signal<AlertConfig[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Pagination
  rows = 20;
  page = signal(0);

  private readonly baseSearchFormConfig: FormFieldConfig[] = [
    {
      key: 'skuCode',
      label: 'alert_settings.search.sku',
      type: 'searchableDropdown',
      placeholder: 'alert_settings.search.select_sku',
      width: '300px',
      showClear: true,
      options: [],
    },
    {
      key: 'warehouseCode',
      label: 'alert_settings.search.warehouse',
      type: 'searchableDropdown',
      placeholder: 'alert_settings.search.select_warehouse',
      width: '300px',
      showClear: true,
      options: [],
    },
    {
      key: 'name',
      label: 'alert_settings.search.name',
      type: 'text',
      placeholder: 'alert_settings.search.enter_name',
      width: '300px',
      showClear: true,
    },
  ];

  searchFormConfig = signal<FormFieldConfig[]>(this.buildSearchConfig());

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      ALERT_SETTINGS_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of ALERT_SETTINGS_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(ALERT_SETTINGS_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      ALERT_SETTINGS_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of ALERT_SETTINGS_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  // Date formatting using formatDateTime utility
  formatDateTime(dateInput: any): string {
    return formatDateTime(dateInput);
  }

  // Search params
  searchParams = signal<AlertConfigSearchParams>({});

  // Confirmation dialog
  showConfirmationDialog = signal(false);
  confirmationTitle = signal('');
  confirmationMessage = signal('');
  selectedConfigForDelete: AlertConfig | null = null;

  ngOnInit(): void {
    this.loadAlertConfigs();
  }

  private buildSearchConfig(): FormFieldConfig[] {
    return withPaginatedSearchFields(this.baseSearchFormConfig, {
      skuCode: {
        loadPage: (params) => this.skuOptionsService.getOptionsPage(params),
      },
      warehouseCode: {
        loadPage: (params) =>
          this.warehouseOptionsService.getOptionsPage(params, {
            status: true,
            valueKey: 'code',
          }),
      },
    });
  }

  ngOnDestroy(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load alert configs with pagination and filtering
   */
  private loadAlertConfigs(): void {
    this.loading.set(true);
    const params = this.searchParams();
    const currentPage = this.page();

    this.alertSettingsService
      .getAlertConfigs(
        currentPage,
        this.rows,
        params.skuCode,
        params.warehouseCode,
        params.name,
        'lastUpdateDate', // Sort by last update date
        false, // Descending (newest first)
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.alertConfigs.set(result.configs);
          this.filteredConfigs.set(result.configs);
          this.totalRecords.set(result.totalRecords);
          this.loading.set(false);
        },
        error: (error: any) => {
          this.loading.set(false);
          const errorMessage = extractErrorMessage(error);
          this.errorDialogService.showError(
            this.errorTranslatorService.translate(
              errorMessage,
              'alert_settings.messages.load_error',
            ),
          );
        },
      });
  }

  /**
   * Handle search
   */
  onSearch(searchData: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      ...searchData,
    });
    this.page.set(0); // Reset to first page
    this.loadAlertConfigs();
  }

  /**
   * Handle reset search
   */
  onReset(): void {
    this.searchParams.set({});
    this.page.set(0); // Reset to first page
    this.loadAlertConfigs();
  }

  /**
   * Handle pagination
   */
  onPageChange(event: any): void {
    this.page.set(event.page);
    this.rows = event.rows;
    this.loadAlertConfigs();
  }

  /**
   * Open add config dialog
   */
  onAdd(): void {
    // Check permission before opening dialog
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    this.dialogRef = this.dialogService.open(AlertSettingsDialogComponent, {
      header: this.translateService.instant('alert_settings.dialog.add_title'),
      width: '650px',
      modal: true,
      closable: true,
      data: {},
    });

    this.dialogRef.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (result) {
          // Dialog closed with success, reload data
          this.page.set(0); // Reset to first page after create
          this.loadAlertConfigs();
        }
      });
  }

  /**
   * Open edit config dialog
   */
  onEdit(config: AlertConfig): void {
    // Check permission before opening dialog
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    this.dialogRef = this.dialogService.open(AlertSettingsDialogComponent, {
      header: this.translateService.instant('alert_settings.dialog.edit_title'),
      width: '500px',
      modal: true,
      closable: true,
      data: {
        config,
      },
    });

    this.dialogRef.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (result) {
          // Dialog closed with success, reload data
          this.loadAlertConfigs();
        }
      });
  }

  /**
   * Handle delete config
   */
  onDelete(config: AlertConfig): void {
    // Check permission before showing confirmation dialog
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    this.selectedConfigForDelete = config;
    this.confirmationTitle.set(
      this.translateService.instant('alert_settings.dialog.delete_title'),
    );
    this.confirmationMessage.set(
      this.translateService.instant('alert_settings.messages.confirm_delete', {
        sku: config.skuCode,
      }),
    );
    this.showConfirmationDialog.set(true);
  }

  /**
   * Handle confirmation dialog action
   */
  onConfirmationDialogAction(action: string): void {
    if (action === 'confirm' && this.selectedConfigForDelete?.id) {
      // Check permission before confirming delete
      if (!this.canDelete()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant(
            'common.permissions.no_permission',
          ),
          detail: this.translateService.instant(
            'common.permissions.no_permission',
          ),
        });
        this.showConfirmationDialog.set(false);
        this.selectedConfigForDelete = null;
        return;
      }

      this.alertSettingsService
        .deleteAlertConfig(this.selectedConfigForDelete.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.page.set(0); // Reset to first page after delete
            this.loadAlertConfigs();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'alert_settings.messages.delete_success',
              ),
            });
          },
          error: (error: any) => {
            const errorMessage = extractErrorMessage(error);
            this.errorDialogService.showError(
              this.errorTranslatorService.translate(
                errorMessage,
                'alert_settings.messages.delete_error',
              ),
            );
          },
        });
    }
    this.showConfirmationDialog.set(false);
    this.selectedConfigForDelete = null;
  }
}
